import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import IntroAnimation from "@/components/IntroAnimation";

const isVideo = (url) => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('.mp4') || lower.includes('.mov') || lower.includes('.webm') || lower.includes('video');
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRIMARY_LOGO = "/pwc-logo-primary.png";
const RING_IMG = "/wrestling-ring.jpg";

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [email, setEmail] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [flyer, setFlyer] = useState(null);
  const [showIntro, setShowIntro] = useState(() => {
    if (sessionStorage.getItem("pwc_intro_seen")) return false;
    return true;
  });

  const handleIntroComplete = useCallback(() => {
    sessionStorage.setItem("pwc_intro_seen", "1");
    setShowIntro(false);
  }, []);

  useEffect(() => {
    axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {});
    axios.get(`${API}/settings`).then(r => {
      if (r.data?.flyer_url) setFlyer(r.data);
    }).catch(() => {});
  }, []);

  const parseDate = (d) => new Date(d?.includes('T') ? d : d + 'T12:00:00');

  const upcomingEvents = events
    .filter(e => parseDate(e.date) >= new Date())
    .sort((a, b) => parseDate(a.date) - parseDate(b.date))
    .slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* Ring Entrance Intro Animation */}
      {showIntro && <IntroAnimation onComplete={handleIntroComplete} />}
      {/* === CRT TV FRAME === */}
      <div className="crt-tv-frame mx-2 sm:mx-4 mt-2">
        <div className="tv-power-led" />
        <div className="tv-brand">PWC Broadcasting</div>
        <div className="crt-screen">

      {/* SHOW OPEN — Title Card */}
      <section className="relative flex flex-col items-center justify-center py-20 sm:py-32 px-4 overflow-hidden min-h-[80vh]">
        {/* Collage background — real vintage wrestling flyers */}
        <div className="absolute inset-0 z-0 bg-[#0D0D0D]">
          <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-4 gap-0">
            <img src="/flyer1.jpg" alt="" className="w-full h-full object-cover" />
            <img src="/flyer2.jpg" alt="" className="w-full h-full object-cover" />
            <img src="/flyer3.jpg" alt="" className="w-full h-full object-cover hidden md:block" />
            <img src="/flyer4.jpg" alt="" className="w-full h-full object-cover hidden md:block" />
          </div>
          <div className="absolute inset-0" style={{ background: 'rgba(13,13,10,0.60)', mixBlendMode: 'normal' }} />
          <div className="absolute inset-0" style={{ filter: 'sepia(50%)', mixBlendMode: 'multiply', background: 'rgba(80,60,20,0.15)' }} />
          <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 180px rgba(0,0,0,0.6)' }} />
        </div>

        {/* Broadcast frame lines */}
        <div className="absolute top-0 left-0 right-0 h-[3px] gold-accent z-10" />
        <div className="absolute bottom-0 left-0 right-0 h-[3px] gold-accent z-10" />
        <div className="absolute top-0 left-0 bottom-0 w-[3px] gold-accent z-10" />
        <div className="absolute top-0 right-0 bottom-0 w-[3px] gold-accent z-10" />

        {/* Show bug */}
        <div className="absolute top-6 right-6 on-air z-10">On Air</div>

        <img
          src={PRIMARY_LOGO}
          alt="Piedmont Wrestling Club"
          data-testid="hero-logo"
          className="relative z-10 w-64 sm:w-80 md:w-96 h-auto mb-10 logo-outline"
        />

        {/* Lower-third style title */}
        <div className="relative z-10 text-center">
          <div className="inline-block segment-label mb-4">Now Presenting</div>
          <h1
            data-testid="hero-tagline"
            className="font-heading text-3xl sm:text-4xl md:text-5xl text-[#E8DFC8] text-center uppercase tracking-tight leading-tight text-outline"
          >
            The Official Voice<br/>of the Territory
          </h1>
          <div className="mt-4 flex items-center justify-center gap-3">
            <div className="h-[2px] w-10 bg-[#B09030]" />
            <p className="font-heading text-sm sm:text-base text-[#B09030] uppercase tracking-[0.3em] text-glow-gold">
              Return to Tradition
            </p>
            <div className="h-[2px] w-10 bg-[#B09030]" />
          </div>
        </div>

        <div className="relative z-10 mt-12 flex flex-col sm:flex-row gap-4">
          <Link
            to="/shows"
            data-testid="hero-events-btn"
            className="gold-accent text-[#1A1510] font-heading uppercase px-10 py-4 text-base sm:text-lg hover:brightness-110 transition-all text-center"
          >
            View Shows
          </Link>
          <Link
            to="/about"
            data-testid="hero-about-btn"
            className="border-2 border-[#B09030] text-[#B09030] font-heading uppercase px-10 py-4 text-base sm:text-lg hover:bg-[#B09030] hover:text-[#1A1510] transition-colors text-center text-outline-sm"
          >
            Our Story
          </Link>
        </div>
      </section>

      {/* TICKER BAR */}
      <div className="broadcast-bar py-3">
        <div className="flex items-center justify-center gap-6 font-heading text-sm sm:text-base text-[#1A1510] uppercase tracking-wider relative z-10">
          <Star className="w-3 h-3 fill-[#1A1510] flex-shrink-0" />
          <span className="font-bold">Follow Us on Facebook &amp; Instagram @Piedmontwrestlingclub</span>
          <Star className="w-3 h-3 fill-[#1A1510] flex-shrink-0" />
          <span className="hidden sm:inline font-bold">Shows Coming Soon to the Piedmont Territory</span>
          <Star className="w-3 h-3 fill-[#1A1510] hidden sm:block flex-shrink-0" />
        </div>
      </div>

      {/* SHOW FLYER — if set from admin */}
      {flyer?.flyer_url && (
        <section className="py-12 sm:py-16 px-4 bg-background">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="segment-label">Now Showing</div>
              <div className="flex-1 h-[1px] bg-foreground/15" />
            </div>
            {flyer.flyer_link ? (
              <a href={flyer.flyer_link} target="_blank" rel="noopener noreferrer" data-testid="homepage-flyer-link" className="block">
                {isVideo(flyer.flyer_url) ? (
                  <video src={flyer.flyer_url} controls className="w-full h-auto shadow-lg" data-testid="homepage-flyer-video" />
                ) : (
                  <img src={flyer.flyer_url} alt="Show Flyer" data-testid="homepage-flyer-image" className="w-full h-auto shadow-lg hover:shadow-xl transition-shadow" />
                )}
              </a>
            ) : (
              isVideo(flyer.flyer_url) ? (
                <video src={flyer.flyer_url} controls className="w-full h-auto shadow-lg" data-testid="homepage-flyer-video" />
              ) : (
                <img src={flyer.flyer_url} alt="Show Flyer" data-testid="homepage-flyer-image" className="w-full h-auto shadow-lg" />
              )
            )}
          </div>
        </section>
      )}

      {/* SEGMENT 1: Welcome */}
      <section className="py-16 sm:py-24 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          {/* Segment header */}
          <div className="flex items-center gap-4 mb-10">
            <div className="segment-label">Segment 01</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
            <div className="md:col-span-7">
              <h2 data-testid="welcome-heading" className="font-heading text-3xl sm:text-4xl uppercase mb-2 leading-tight">
                Welcome to<br/>the Territory
              </h2>
              <div className="h-[3px] w-16 gold-accent mb-8" />
              <div className="font-body text-base leading-relaxed space-y-5 text-foreground">
                <p className="first-letter:text-4xl first-letter:font-heading first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:leading-none">
                  The Piedmont Wrestling Club brings back what made professional wrestling great
                  — the grit, the drama, and the unmatched energy of territory-era wrestling.
                  This isn't sports entertainment. This is <em>professional wrestling.</em>
                </p>
                <p>
                  No gimmicks. No shortcuts. No million-dollar light shows covering up
                  five-minute matches. Just hard-hitting, bell-to-bell action that puts
                  the wrestling back where it belongs — in the ring.
                </p>
              </div>
              {/* Chyron-style callout */}
              <div className="chyron mt-8 inline-block">
                <p className="font-heading text-base text-[#B09030] uppercase tracking-wider text-glow-gold">
                  The territory is open for business.
                </p>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="relative tv-vignette">
                <img
                  src={RING_IMG}
                  alt="Wrestling Ring"
                  className="w-full h-72 sm:h-96 object-cover vintage-img"
                />
                <div className="absolute bottom-0 left-0 right-0 h-[3px] gold-accent" />
                <div className="absolute top-3 right-3">
                  <div className="segment-label">Est. 2026</div>
                </div>
              </div>
              {/* Stat cards */}
              <div className="grid grid-cols-3 gap-0 mt-0">
                <div className="bg-[#0D0D0D] p-4 text-center border-r border-[#E8DFC8]/5">
                  <p className="font-heading text-lg text-[#B09030]">PWC</p>
                  <p className="font-body text-[9px] text-[#E8DFC8]/40 uppercase tracking-wider">Promotion</p>
                </div>
                <div className="bg-[#8B3020] p-4 text-center">
                  <p className="font-heading text-lg text-[#E8DFC8]">2026</p>
                  <p className="font-body text-[9px] text-[#E8DFC8]/50 uppercase tracking-wider">Founded</p>
                </div>
                <div className="bg-[#0D0D0D] p-4 text-center border-l border-[#E8DFC8]/5">
                  <p className="font-heading text-lg text-[#B09030]">Piedmont</p>
                  <p className="font-body text-[9px] text-[#E8DFC8]/40 uppercase tracking-wider">Territory</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EMAIL SIGNUP */}
      <section className="studio-dark py-14 sm:py-20 px-4 relative">
        <div className="absolute top-0 left-0 right-0 h-[3px] gold-accent" />
        <div className="max-w-xl mx-auto text-center">
          <div className="segment-label mx-auto mb-6 inline-block">Join the Territory</div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#E8DFC8] uppercase mb-3">
            Get on the List
          </h2>
          <p className="font-body text-sm text-[#E8DFC8]/50 mb-8">
            Be the first to know about upcoming shows, talent announcements, and territory news.
          </p>
          <form
            data-testid="email-signup-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!email.trim()) return;
              setSubLoading(true);
              try {
                const { data } = await axios.post(`${API}/subscribe`, { email });
                toast.success(data.message);
                setEmail("");
              } catch {
                toast.error("Something went wrong. Try again.");
              } finally {
                setSubLoading(false);
              }
            }}
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <Input
              type="email"
              data-testid="email-signup-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Your email address"
              required
              className="flex-1 rounded-none border-none bg-[#E8DFC8]/10 text-[#E8DFC8] placeholder:text-[#E8DFC8]/30 font-body text-sm h-12"
            />
            <button
              type="submit"
              data-testid="email-signup-btn"
              disabled={subLoading}
              className="gold-accent text-[#1A1510] font-heading uppercase px-8 h-12 text-sm hover:brightness-110 transition-all flex-shrink-0"
            >
              {subLoading ? "..." : "Sign Up"}
            </button>
          </form>
          <p className="font-body text-[10px] text-[#E8DFC8]/25 mt-4 uppercase tracking-wider">
            No spam. Territory news only.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[3px] gold-accent" />
      </section>

      {/* INTERSTITIAL — Quote */}
      <section className="studio-dark py-14 sm:py-20 px-4 relative tv-vignette">
        <div className="absolute top-0 left-0 right-0 h-[3px] gold-accent" />
        <div className="max-w-4xl mx-auto text-center">
          <div className="segment-label mx-auto mb-8 inline-block">Featured</div>
          <blockquote className="font-heading text-2xl sm:text-3xl md:text-4xl text-[#E8DFC8] uppercase leading-snug">
            "Where the ropes are tight,<br/>the competition is real, and every<br/>match tells a story."
          </blockquote>
          <div className="flex items-center justify-center gap-3 mt-6">
            <div className="h-[2px] w-8 bg-[#B09030]" />
            <p className="font-body text-xs text-[#B09030] uppercase tracking-[0.3em]">
              Piedmont Wrestling Club
            </p>
            <div className="h-[2px] w-8 bg-[#B09030]" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[3px] gold-accent" />
      </section>

      {/* SEGMENT 2: Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="py-16 sm:py-24 px-4 bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-10">
              <div className="segment-label">Up Next</div>
              <div className="flex-1 h-[1px] bg-foreground/15" />
            </div>
            <h2 data-testid="upcoming-events-heading" className="font-heading text-3xl sm:text-4xl uppercase mb-2">
              Cards &amp; Shows
            </h2>
            <div className="h-[3px] w-16 gold-accent mb-10" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {upcomingEvents.map((event) => (
                <div key={event.id} data-testid={`event-card-${event.id}`} className="border-l-4 border-[#8B3020] pl-6">
                  <p className="font-body text-xs text-muted-foreground uppercase tracking-widest mb-1">
                    {parseDate(event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  <h3 className="font-heading text-xl uppercase mb-2">{event.title}</h3>
                  {event.venue && (
                    <p className="font-body text-sm text-muted-foreground">{event.venue}{event.city ? `, ${event.city}` : ''}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-10">
              <Link
                to="/shows"
                data-testid="view-all-events-link"
                className="font-heading uppercase text-[#8B3020] hover:text-foreground transition-colors tracking-wider"
              >
                View Full Schedule &rarr;
              </Link>
            </div>
          </div>
        </section>
      )}

        </div>{/* end crt-screen */}
      </div>{/* end crt-tv-frame */}
    </div>
  );
}
